import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, intlShape, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import JSZip from 'jszip';
import log from '../lib/log';
import sharedMessages from './shared-messages';
import styles from './sb-file-uploader-hoc.css';

import {
    LoadingStates,
    getIsLoadingUpload,
    getIsShowingWithoutId,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';
import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';
import {
    closeFileMenu
} from '../reducers/menus';

const messages = defineMessages({
    loadError: {
        id: 'gui.projectLoader.loadError',
        defaultMessage: 'The project file that was selected failed to load.',
        description: 'An error that displays when a local project file fails to load.'
    },
    extremeProjectOnly: {
        id: 'gui.projectLoader.extremeProjectOnly',
        defaultMessage: 'This project is for the eXtreme module. Please switch to eXtreme from the module menu above and try loading it again.',
        description: 'Error shown when an eXtreme project is opened in another module'
    },
    xlineProjectOnly: {
        id: 'gui.projectLoader.xlineProjectOnly',
        defaultMessage: 'This project is for the X-LINE module. Please switch to X-LINE from the module menu above and try loading it again.',
        description: 'Error shown when an X-LINE project is opened in another module'
    },
    compatibilityModalTitle: {
        id: 'gui.projectLoader.compatibilityModalTitle',
        defaultMessage: 'This file cannot be opened here.',
        description: 'Title shown in the custom modal when the project module is incompatible'
    },
    compatibilityModalConfirm: {
        id: 'gui.projectLoader.compatibilityModalConfirm',
        defaultMessage: 'OK',
        description: 'Confirm button label for incompatible project modal'
    }
});

const getLocalizedLabel = (locale, english, korean) => (locale === 'ko' ? korean : english);

/**
 * Higher Order Component to provide behavior for loading local project files into editor.
 * @param {React.Component} WrappedComponent the component to add project file loading functionality to
 * @returns {React.Component} WrappedComponent with project file loading functionality added
 *
 * <SBFileUploaderHOC>
 *     <WrappedComponent />
 * </SBFileUploaderHOC>
 */
const SBFileUploaderHOC = function (WrappedComponent) {
    class SBFileUploaderComponent extends React.Component {
        constructor (props) {
            super(props);
            this.state = {
                compatibilityErrorMessage: null,
                isReplaceConfirmOpen: false
            };
            bindAll(this, [
                'checkModuleCompatibility',
                'closeCompatibilityModal',
                'closeReplaceConfirmModal',
                'confirmReplaceAndUpload',
                'createFileObjects',
                'getProjectTitleFromFilename',
                'handleFinishedLoadingUpload',
                'handleStartSelectingFileUpload',
                'handleChange',
                'onload',
                'removeFileObjects'
            ]);
        }
        componentDidUpdate (prevProps) {
            if (this.props.isLoadingUpload && !prevProps.isLoadingUpload) {
                this.handleFinishedLoadingUpload(); // cue step 5 below
            }
        }
        componentWillUnmount () {
            this.removeFileObjects();
        }
        closeCompatibilityModal () {
            this.setState({compatibilityErrorMessage: null});
        }
        closeReplaceConfirmModal () {
            this.setState({isReplaceConfirmOpen: false});
            this.removeFileObjects();
            this.props.closeFileMenu();
        }
        confirmReplaceAndUpload () {
            this.setState({isReplaceConfirmOpen: false});
            this.props.requestProjectUpload(this.props.loadingState);
            this.props.closeFileMenu();
        }
        // step 1: this is where the upload process begins
        handleStartSelectingFileUpload () {
            this.createFileObjects(); // go to step 2
        }
        // step 2: create a FileReader and an <input> element, and issue a
        // pseudo-click to it. That will open the file chooser dialog.
        createFileObjects () {
            // redo step 7, in case it got skipped last time and its objects are
            // still in memory
            this.removeFileObjects();
            // create fileReader
            this.fileReader = new FileReader();
            this.fileReader.onload = this.onload;
            // create <input> element and add it to DOM
            this.inputElement = document.createElement('input');
            this.inputElement.accept = '.xc,.sb,.sb2,.sb3';
            this.inputElement.style = 'display: none;';
            this.inputElement.type = 'file';
            this.inputElement.onchange = this.handleChange; // connects to step 3
            document.body.appendChild(this.inputElement);
            // simulate a click to open file chooser dialog
            this.inputElement.click();
        }
        // step 3: user has picked a file using the file chooser dialog.
        // We don't actually load the file here, we only decide whether to do so.
        handleChange (e) {
            const {
                isShowingWithoutId,
                loadingState,
                projectChanged,
                userOwnsProject
            } = this.props;
            const thisFileInput = e.target;
            if (thisFileInput.files) { // Don't attempt to load if no file was selected
                this.fileToUpload = thisFileInput.files[0];

                // If user owns the project, or user has changed the project,
                // we must confirm with the user that they really intend to
                // replace it. (If they don't own the project and haven't
                // changed it, no need to confirm.)
                if (userOwnsProject || (projectChanged && isShowingWithoutId)) {
                    this.props.closeFileMenu();
                    this.setState({isReplaceConfirmOpen: true});
                } else {
                    // cues step 4
                    this.props.requestProjectUpload(loadingState);
                    this.props.closeFileMenu();
                }
            }
        }
        // step 4 is below, in mapDispatchToProps

        // step 5: called from componentDidUpdate when project state shows
        // that project data has finished "uploading" into the browser
        handleFinishedLoadingUpload () {
            if (this.fileToUpload && this.fileReader) {
                // begin to read data from the file. When finished,
                // cues step 6 using the reader's onload callback
                this.fileReader.readAsArrayBuffer(this.fileToUpload);
            } else {
                this.props.cancelFileUpload(this.props.loadingState);
                // skip ahead to step 7
                this.removeFileObjects();
            }
        }
        // used in step 6 below
        getProjectTitleFromFilename (fileInputFilename) {
            if (!fileInputFilename) return '';
            // Parse title from supported project extensions.
            const matches = fileInputFilename.match(/^(.*)\.(xc|sb|sb2|sb3)$/i);
            if (!matches) return '';
            return matches[1].substring(0, 100); // truncate project title to max 100 chars
        }
        async checkModuleCompatibility (projectBuffer) {
            const currentModule = new URLSearchParams(window.location.search).get('module') || 'default';
            if (currentModule !== 'extreme' && currentModule !== 'xline') {
                return null;
            }

            let projectJSON = null;
            try {
                const zip = await JSZip.loadAsync(projectBuffer);
                const projectFile = zip.file('project.json');
                if (projectFile) {
                    projectJSON = JSON.parse(await projectFile.async('string'));
                }
            } catch (error) {
                try {
                    const rawText = new TextDecoder().decode(projectBuffer);
                    projectJSON = JSON.parse(rawText);
                } catch (jsonError) {
                    log.warn('Could not inspect project module compatibility', error || jsonError);
                    return null;
                }
            }

            if (!projectJSON || !Array.isArray(projectJSON.targets)) {
                return null;
            }

            let hasExtremeBlocks = false;
            let hasXlineBlocks = false;
            for (const target of projectJSON.targets) {
                const blocks = target && target.blocks ? Object.values(target.blocks) : [];
                for (const block of blocks) {
                    const opcode = block && block.opcode;
                    if (typeof opcode !== 'string') continue;
                    if (opcode.startsWith('xrobo_')) hasExtremeBlocks = true;
                    if (opcode.startsWith('xline_')) hasXlineBlocks = true;
                    if (hasExtremeBlocks && hasXlineBlocks) break;
                }
                if (hasExtremeBlocks && hasXlineBlocks) break;
            }

            if (currentModule === 'extreme' && hasXlineBlocks) {
                return this.props.intl.formatMessage(messages.xlineProjectOnly);
            }
            if (currentModule === 'xline' && hasExtremeBlocks) {
                return this.props.intl.formatMessage(messages.extremeProjectOnly);
            }
            return null;
        }
        // step 6: attached as a handler on our FileReader object; called when
        // file upload raw data is available in the reader
        onload () {
            if (this.fileReader) {
                this.props.onLoadingStarted();
                const filename = this.fileToUpload && this.fileToUpload.name;
                let loadingSuccess = false;
                Promise.resolve(this.checkModuleCompatibility(this.fileReader.result))
                    .then(moduleCompatibilityError => {
                        if (moduleCompatibilityError) {
                            this.setState({
                                compatibilityErrorMessage: moduleCompatibilityError
                            });
                            return null;
                        }
                        return this.props.vm.loadProject(this.fileReader.result)
                            .then(() => {
                                if (filename) {
                                    const uploadedProjectTitle = this.getProjectTitleFromFilename(filename);
                                    this.props.onSetProjectTitle(uploadedProjectTitle);
                                }
                                loadingSuccess = true;
                            });
                    })
                    .catch(error => {
                        log.warn(error);
                        alert(this.props.intl.formatMessage(messages.loadError)); // eslint-disable-line no-alert
                    })
                    .then(() => {
                        this.props.onLoadingFinished(this.props.loadingState, loadingSuccess);
                        // go back to step 7: whether project loading succeeded
                        // or failed, reset file objects
                        this.removeFileObjects();
                    });
            }
        }
        // step 7: remove the <input> element from the DOM and clear reader and
        // fileToUpload reference, so those objects can be garbage collected
        removeFileObjects () {
            if (this.inputElement) {
                this.inputElement.value = null;
                document.body.removeChild(this.inputElement);
            }
            this.inputElement = null;
            this.fileReader = null;
            this.fileToUpload = null;
        }
        render () {
            const {
                /* eslint-disable no-unused-vars */
                cancelFileUpload,
                closeFileMenu: closeFileMenuProp,
                isLoadingUpload,
                isShowingWithoutId,
                loadingState,
                onLoadingFinished,
                onLoadingStarted,
                onSetProjectTitle,
                projectChanged,
                requestProjectUpload: requestProjectUploadProp,
                userOwnsProject,
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;
            return (
                <React.Fragment>
                    <WrappedComponent
                        onStartSelectingFileUpload={this.handleStartSelectingFileUpload}
                        {...componentProps}
                    />
                    {this.state.compatibilityErrorMessage ? (
                        <div className={styles.compatibilityModalOverlay}>
                            <div className={styles.compatibilityModalCard}>
                                <div className={styles.compatibilityModalTitle}>
                                    {this.props.intl.formatMessage(messages.compatibilityModalTitle)}
                                </div>
                                <div className={styles.compatibilityModalDescription}>
                                    {this.state.compatibilityErrorMessage}
                                </div>
                                <div className={styles.compatibilityModalActions}>
                                    <button
                                        className={styles.compatibilityModalButton}
                                        onClick={this.closeCompatibilityModal}
                                        type="button"
                                    >
                                        {this.props.intl.formatMessage(messages.compatibilityModalConfirm)}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {this.state.isReplaceConfirmOpen ? (
                        <div className={styles.compatibilityModalOverlay}>
                            <div className={styles.compatibilityModalCard}>
                                <div className={styles.compatibilityModalTitle}>
                                    {getLocalizedLabel(this.props.intl.locale, 'Load this project?', '파일을 불러오시겠습니까?')}
                                </div>
                                <div className={styles.compatibilityModalDescription}>
                                    {getLocalizedLabel(this.props.intl.locale, 'Your current work may be removed.', '현재 작업 내용이 사라질 수 있습니다.')}
                                </div>
                                <div className={styles.compatibilityModalActions}>
                                    <button
                                        className={styles.compatibilityModalButtonSecondary}
                                        onClick={this.closeReplaceConfirmModal}
                                        type="button"
                                    >
                                        {getLocalizedLabel(this.props.intl.locale, 'Cancel', '취소')}
                                    </button>
                                    <button
                                        className={styles.compatibilityModalButton}
                                        onClick={this.confirmReplaceAndUpload}
                                        type="button"
                                    >
                                        {getLocalizedLabel(this.props.intl.locale, 'Load', '불러오기')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </React.Fragment>
            );
        }
    }

    SBFileUploaderComponent.propTypes = {
        canSave: PropTypes.bool,
        cancelFileUpload: PropTypes.func,
        closeFileMenu: PropTypes.func,
        intl: intlShape.isRequired,
        isLoadingUpload: PropTypes.bool,
        isShowingWithoutId: PropTypes.bool,
        loadingState: PropTypes.oneOf(LoadingStates),
        onLoadingFinished: PropTypes.func,
        onLoadingStarted: PropTypes.func,
        onSetProjectTitle: PropTypes.func,
        projectChanged: PropTypes.bool,
        requestProjectUpload: PropTypes.func,
        userOwnsProject: PropTypes.bool,
        vm: PropTypes.shape({
            loadProject: PropTypes.func
        })
    };
    const mapStateToProps = (state, ownProps) => {
        const loadingState = state.scratchGui.projectState.loadingState;
        const user = state.session && state.session.session && state.session.session.user;
        return {
            isLoadingUpload: getIsLoadingUpload(loadingState),
            isShowingWithoutId: getIsShowingWithoutId(loadingState),
            loadingState: loadingState,
            projectChanged: state.scratchGui.projectChanged,
            userOwnsProject: ownProps.authorUsername && user &&
                (ownProps.authorUsername === user.username),
            vm: state.scratchGui.vm
        };
    };
    const mapDispatchToProps = (dispatch, ownProps) => ({
        cancelFileUpload: loadingState => dispatch(onLoadedProject(loadingState, false, false)),
        closeFileMenu: () => dispatch(closeFileMenu()),
        // transition project state from loading to regular, and close
        // loading screen and file menu
        onLoadingFinished: (loadingState, success) => {
            dispatch(onLoadedProject(loadingState, ownProps.canSave, success));
            dispatch(closeLoadingProject());
            dispatch(closeFileMenu());
        },
        // show project loading screen
        onLoadingStarted: () => dispatch(openLoadingProject()),
        onSetProjectTitle: title => dispatch(setProjectTitle(title)),
        // step 4: transition the project state so we're ready to handle the new
        // project data. When this is done, the project state transition will be
        // noticed by componentDidUpdate()
        requestProjectUpload: loadingState => dispatch(requestProjectUpload(loadingState))
    });
    // Allow incoming props to override redux-provided props. Used to mock in tests.
    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );
    return injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(SBFileUploaderComponent));
};

export {
    SBFileUploaderHOC as default
};
